import {
    Fragment,
    FunctionComponent,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    List,
    ListItem,
    ListItemText,
    MenuItem,
    Stack,
    SvgIcon,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import Grid2 from '@mui/material/Grid2';
import { LoadingButton } from '@mui/lab';
import { ArrowDownTrayIcon } from '@heroicons/react/24/solid';
import {
    createEmptyIssueVoucherForm,
    formatIssuer,
    getIssueVoucherErrorMessage,
    ISSUE_VOUCHER_LIMITS,
    ISSUED_VOUCHER_STATUS_LABELS,
    IssuedVoucherDTO,
    IssuedVoucherMoney,
    IssueVoucherFormErrors,
    IssueVoucherFormValues,
    resultOfCurrentQuery,
    todayAsIsoDate,
    toIssueVouchersCommand,
    useGetIssuedVouchersQuery,
    useGetIssueVoucherTemplatesQuery,
    useIssueVouchersMutation,
    validateIssueVoucherAmount,
    validateIssueVoucherForm,
} from '@starjack/entities/issuedVoucher';
import { useNotification } from '@starjack/features/notifications';
import { useAuthentication } from '@starjack/features/authentication';
import { StajackTableColumn, StarjackTable } from '@starjack/shared-components';
import { useDebouncedEffect } from '@starjack/utils';
import {
    downloadIssuedVoucherPdf,
    fetchIssueVoucherPreview,
} from './issuedVoucherDownload';

const formatMoney = (money?: IssuedVoucherMoney | null): string => {
    if (!money) return '–';
    return new Intl.NumberFormat('de-DE', {
        style: 'currency',
        currency: money.currency || 'EUR',
    }).format(money.amount);
};

/** Formats a YYYY-MM-DD date without going through the time zone. */
const formatIsoDate = (value?: string | null): string => {
    if (!value) return 'unbefristet';
    const [year, month, day] = value.split('-');
    return day && month && year ? `${day}.${month}.${year}` : value;
};

const formatDateTime = (value?: string | null): string => {
    if (!value) return '–';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString('de-DE', {
        dateStyle: 'medium',
        timeStyle: 'short',
    });
};

const statusColor = (
    status: IssuedVoucherDTO['status']
): 'success' | 'default' | 'error' => {
    if (status === 'PAID') return 'success';
    if (status === 'REVOKED') return 'error';
    return 'default';
};

type IssuedVouchersContentProps = {
    /** Sub-user id → username, to resolve the issuer column. */
    issuerNames?: Record<string, string>;
};

export const IssuedVouchersContent: FunctionComponent<
    IssuedVouchersContentProps
> = ({ issuerNames }) => {
    const { addNotification } = useNotification();
    const { liftOperatorData } = useAuthentication();
    const liftOperatorId = liftOperatorData?.liftOperatorId;

    const {
        data: templates = [],
        isLoading: templatesLoading,
        error: templatesError,
    } = useGetIssueVoucherTemplatesQuery();
    const [issueVouchers, { isLoading: issuing }] = useIssueVouchersMutation();

    const [values, setValues] = useState<IssueVoucherFormValues>(
        createEmptyIssueVoucherForm
    );
    const [errors, setErrors] = useState<IssueVoucherFormErrors>({});
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [issuedNow, setIssuedNow] = useState<IssuedVoucherDTO[]>([]);
    const [downloadingId, setDownloadingId] = useState<string | null>(null);

    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const previewUrlRef = useRef<string | null>(null);
    // Incremented whenever a pending preview response must be discarded
    // (new request, dialog closed, page left).
    const previewRequestRef = useRef(0);

    const [localCode, setLocalCode] = useState('');
    const [search, setSearch] = useState<{
        code?: string;
        page: number;
        size: number;
    }>({ page: 0, size: 10 });

    const {
        data: lastIssuedVouchers,
        isFetching: issuedVouchersLoading,
        error: issuedVouchersError,
    } = useGetIssuedVouchersQuery(search);
    const issuedVouchers = resultOfCurrentQuery(
        lastIssuedVouchers,
        issuedVouchersError
    );

    const selectedTemplate = useMemo(
        () => templates.find((t) => t.id === values.voucherTemplateId),
        [templates, values.voucherTemplateId]
    );

    useDebouncedEffect(
        () => {
            const code = localCode.trim() || undefined;
            // Only reset the page when the effective search changed; otherwise
            // the table would keep showing its old page for page 0 data.
            setSearch((prev) =>
                prev.code === code ? prev : { ...prev, code, page: 0 }
            );
        },
        450,
        [localCode]
    );

    const revokePreviewUrl = useCallback(() => {
        if (previewUrlRef.current) {
            URL.revokeObjectURL(previewUrlRef.current);
            previewUrlRef.current = null;
        }
        setPreviewUrl(null);
    }, []);

    // Release a still open preview image and drop pending responses when the
    // page is left.
    useEffect(() => {
        const requestRef = previewRequestRef;
        return () => {
            requestRef.current += 1;
            revokePreviewUrl();
        };
    }, [revokePreviewUrl]);

    const setField =
        (key: keyof IssueVoucherFormValues) =>
        (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            const { value } = e.target;
            setValues((prev) => ({ ...prev, [key]: value }));
            setErrors((prev) => ({ ...prev, [key]: undefined }));
        };

    const onTemplateChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        const nextId = e.target.value;
        const nextTemplate = templates.find((t) => t.id === nextId);
        setValues((prev) => {
            const previousDefault = templates.find(
                (t) => t.id === prev.voucherTemplateId
            )?.defaultText;
            // Keep text the user typed; only replace an empty or untouched default text.
            const keepText =
                prev.text.trim() !== '' && prev.text !== previousDefault;
            return {
                ...prev,
                voucherTemplateId: nextId,
                text: keepText ? prev.text : (nextTemplate?.defaultText ?? ''),
            };
        });
        setErrors((prev) => ({
            ...prev,
            voucherTemplateId: undefined,
            text: undefined,
        }));
    };

    const onPreview = async () => {
        const amountError = validateIssueVoucherAmount(values.amount);
        const nextErrors: IssueVoucherFormErrors = {};
        if (!selectedTemplate) {
            nextErrors.voucherTemplateId = 'Bitte eine Vorlage wählen.';
        }
        if (amountError) nextErrors.amount = amountError;
        if (Object.keys(nextErrors).length > 0) {
            setErrors((prev) => ({ ...prev, ...nextErrors }));
            return;
        }

        revokePreviewUrl();
        setPreviewOpen(true);
        setPreviewLoading(true);
        previewRequestRef.current += 1;
        const requestId = previewRequestRef.current;
        const url = await fetchIssueVoucherPreview({
            voucherTemplateId: selectedTemplate.id,
            amount: { amount: Number(values.amount.trim()), currency: 'EUR' },
            sender: values.sender,
            receiver: values.receiver,
            text: values.text || selectedTemplate.defaultText,
            language: selectedTemplate.language,
        });
        if (requestId !== previewRequestRef.current) {
            // Stale response: a newer preview was requested or the dialog closed.
            if (url) URL.revokeObjectURL(url);
            return;
        }
        previewUrlRef.current = url;
        setPreviewUrl(url);
        setPreviewLoading(false);
        if (!url) {
            addNotification({
                type: 'error',
                message:
                    'Vorschau konnte nicht generiert werden. Bitte erneut versuchen.',
            });
        }
    };

    const onClosePreview = () => {
        previewRequestRef.current += 1;
        setPreviewOpen(false);
        setPreviewLoading(false);
        revokePreviewUrl();
    };

    const onRequestIssue = () => {
        const nextErrors = validateIssueVoucherForm(values, todayAsIsoDate());
        setErrors(nextErrors);
        if (Object.keys(nextErrors).length === 0) {
            setConfirmOpen(true);
        }
    };

    const onConfirmIssue = async () => {
        try {
            const result = await issueVouchers(
                toIssueVouchersCommand(values)
            ).unwrap();
            const vouchers = result?.vouchers ?? [];
            setIssuedNow(vouchers);
            addNotification({
                type: 'success',
                message:
                    vouchers.length === 1
                        ? 'Gutschein ausgestellt.'
                        : `${vouchers.length} Gutscheine ausgestellt.`,
            });
            // Force a new reason for the next batch so it is not reused by accident.
            setValues((prev) => ({ ...prev, note: '' }));
        } catch (err) {
            addNotification({
                type: 'error',
                message: getIssueVoucherErrorMessage(err),
            });
        } finally {
            setConfirmOpen(false);
        }
    };

    const onDownload = useCallback(
        async (voucher: IssuedVoucherDTO) => {
            setDownloadingId(voucher.id);
            const error = await downloadIssuedVoucherPdf(voucher);
            setDownloadingId(null);
            if (error) {
                addNotification({ type: 'error', message: error });
            }
        },
        [addNotification]
    );

    const renderDownloadButton = (voucher: IssuedVoucherDTO) => (
        <Tooltip title='PDF herunterladen'>
            <span>
                <IconButton
                    size='small'
                    disabled={downloadingId === voucher.id}
                    onClick={() => onDownload(voucher)}
                >
                    {downloadingId === voucher.id ? (
                        <CircularProgress size={18} />
                    ) : (
                        <SvgIcon fontSize='small'>
                            <ArrowDownTrayIcon />
                        </SvgIcon>
                    )}
                </IconButton>
            </span>
        </Tooltip>
    );

    const quantity = Number(values.quantity) || 0;
    const amount = Number(values.amount) || 0;
    const formattedAmount = formatMoney({ amount, currency: 'EUR' });
    const formattedTotal = formatMoney({
        amount: amount * quantity,
        currency: 'EUR',
    });

    const columns: StajackTableColumn<IssuedVoucherDTO>[] = [
        { label: 'Code', itemKey: 'code' },
        {
            label: 'Betrag',
            itemTransform: (v) => formatMoney(v.initialAmount),
        },
        {
            label: 'Restwert',
            itemTransform: (v) => formatMoney(v.redeemableAmount),
        },
        {
            label: 'Status',
            itemTransform: (v) => (
                <Chip
                    size='small'
                    label={ISSUED_VOUCHER_STATUS_LABELS[v.status] ?? v.status}
                    color={statusColor(v.status)}
                />
            ),
        },
        {
            label: 'Gültig bis',
            itemTransform: (v) => formatIsoDate(v.validUntil),
        },
        {
            label: 'Ausgestellt am',
            itemTransform: (v) => formatDateTime(v.issuedAt),
        },
        {
            label: 'Ausgestellt von',
            itemTransform: (v) =>
                formatIssuer(v.issuedBy, liftOperatorId, issuerNames),
        },
        {
            label: 'Notiz',
            itemTransform: (v) => (
                <Typography
                    variant='body2'
                    sx={{ maxWidth: 240, overflowWrap: 'anywhere' }}
                >
                    {v.note || '–'}
                </Typography>
            ),
        },
        {
            label: 'PDF',
            itemTransform: (v) => renderDownloadButton(v),
        },
    ];

    const noTemplates =
        !templatesLoading && !templatesError && !templates.length;
    const limits = ISSUE_VOUCHER_LIMITS;

    return (
        <Fragment>
            <Typography variant='h4' sx={{ mb: 4 }}>
                Gutscheine ausstellen
            </Typography>

            <Stack spacing={4} alignItems='flex-start'>
                <Card sx={{ width: '100%' }}>
                    <CardContent>
                        {!!templatesError && (
                            <Alert severity='error' sx={{ mb: 3 }}>
                                {getIssueVoucherErrorMessage(
                                    templatesError,
                                    'Die Gutscheinvorlagen konnten nicht geladen werden. Bitte später noch einmal versuchen.'
                                )}
                            </Alert>
                        )}
                        {noTemplates && (
                            <Alert severity='info' sx={{ mb: 3 }}>
                                Es gibt keine aktive, vollständige
                                Gutscheinvorlage. Bitte zuerst unter
                                „Gutscheinvorlagen“ eine Vorlage anlegen und
                                aktivieren.
                            </Alert>
                        )}

                        <Grid2 container spacing={3}>
                            <Grid2 size={{ xs: 12, md: 6 }}>
                                <TextField
                                    select
                                    fullWidth
                                    required
                                    label='Vorlage'
                                    value={values.voucherTemplateId}
                                    onChange={onTemplateChange}
                                    error={!!errors.voucherTemplateId}
                                    helperText={errors.voucherTemplateId}
                                    disabled={!templates.length}
                                >
                                    {templates.map((template) => (
                                        <MenuItem
                                            key={template.id}
                                            value={template.id}
                                        >
                                            {template.name} ({template.language}
                                            )
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid2>
                            <Grid2 size={{ xs: 6, md: 3 }}>
                                <TextField
                                    fullWidth
                                    required
                                    label='Betrag in EUR'
                                    type='number'
                                    value={values.amount}
                                    onChange={setField('amount')}
                                    error={!!errors.amount}
                                    helperText={
                                        errors.amount ||
                                        `Ganze Euro, ${limits.minAmount}–${limits.maxAmount.toLocaleString('de-DE')} €`
                                    }
                                    slotProps={{
                                        htmlInput: {
                                            min: limits.minAmount,
                                            max: limits.maxAmount,
                                            step: 1,
                                        },
                                    }}
                                />
                            </Grid2>
                            <Grid2 size={{ xs: 6, md: 3 }}>
                                <TextField
                                    fullWidth
                                    required
                                    label='Anzahl'
                                    type='number'
                                    value={values.quantity}
                                    onChange={setField('quantity')}
                                    error={!!errors.quantity}
                                    helperText={
                                        errors.quantity ||
                                        `${limits.minQuantity}–${limits.maxQuantity} Stück`
                                    }
                                    slotProps={{
                                        htmlInput: {
                                            min: limits.minQuantity,
                                            max: limits.maxQuantity,
                                            step: 1,
                                        },
                                    }}
                                />
                            </Grid2>
                            <Grid2 size={{ xs: 12, md: 6 }}>
                                <TextField
                                    fullWidth
                                    label='Gültig bis'
                                    type='date'
                                    value={values.validUntil}
                                    onChange={setField('validUntil')}
                                    error={!!errors.validUntil}
                                    helperText={
                                        errors.validUntil ||
                                        'Leer = unbefristet. Das Datum wird nicht auf den Gutschein gedruckt – bei Bedarf im Text erwähnen.'
                                    }
                                    slotProps={{
                                        inputLabel: { shrink: true },
                                        htmlInput: { min: todayAsIsoDate() },
                                    }}
                                />
                            </Grid2>
                            <Grid2 size={{ xs: 12, md: 3 }}>
                                <TextField
                                    fullWidth
                                    label='Absender'
                                    value={values.sender}
                                    onChange={setField('sender')}
                                    error={!!errors.sender}
                                    helperText={
                                        errors.sender ||
                                        `Optional, max. ${limits.maxNameLength} Zeichen`
                                    }
                                    slotProps={{
                                        htmlInput: {
                                            maxLength: limits.maxNameLength,
                                        },
                                    }}
                                />
                            </Grid2>
                            <Grid2 size={{ xs: 12, md: 3 }}>
                                <TextField
                                    fullWidth
                                    label='Empfänger'
                                    value={values.receiver}
                                    onChange={setField('receiver')}
                                    error={!!errors.receiver}
                                    helperText={
                                        errors.receiver ||
                                        `Optional, max. ${limits.maxNameLength} Zeichen`
                                    }
                                    slotProps={{
                                        htmlInput: {
                                            maxLength: limits.maxNameLength,
                                        },
                                    }}
                                />
                            </Grid2>
                            <Grid2 size={12}>
                                <TextField
                                    fullWidth
                                    multiline
                                    minRows={3}
                                    label='Text'
                                    value={values.text}
                                    onChange={setField('text')}
                                    error={!!errors.text}
                                    helperText={
                                        errors.text ||
                                        `${values.text.length}/${limits.maxTextLength} Zeichen. Leer = Standardtext der Vorlage.`
                                    }
                                    slotProps={{
                                        htmlInput: {
                                            maxLength: limits.maxTextLength,
                                        },
                                    }}
                                />
                            </Grid2>
                            <Grid2 size={12}>
                                <TextField
                                    fullWidth
                                    required
                                    multiline
                                    minRows={2}
                                    label='Interne Notiz / Grund'
                                    value={values.note}
                                    onChange={setField('note')}
                                    error={!!errors.note}
                                    helperText={
                                        errors.note ||
                                        'Nur intern sichtbar, z. B. Sturmtag 12.01.'
                                    }
                                    slotProps={{
                                        htmlInput: {
                                            maxLength: limits.maxNoteLength,
                                        },
                                    }}
                                />
                            </Grid2>
                        </Grid2>

                        <Stack
                            direction='row'
                            spacing={2}
                            justifyContent='flex-end'
                            sx={{ mt: 3 }}
                        >
                            <Button
                                variant='outlined'
                                onClick={onPreview}
                                disabled={!templates.length}
                            >
                                Vorschau
                            </Button>
                            <LoadingButton
                                variant='contained'
                                onClick={onRequestIssue}
                                loading={issuing}
                                disabled={!templates.length}
                            >
                                Gutschein(e) ausstellen
                            </LoadingButton>
                        </Stack>
                    </CardContent>
                </Card>

                {issuedNow.length > 0 && (
                    <Card sx={{ width: '100%' }}>
                        <CardContent>
                            <Typography variant='h6' sx={{ mb: 1 }}>
                                Soeben ausgestellt
                            </Typography>
                            <List dense>
                                {issuedNow.map((voucher) => (
                                    <ListItem
                                        key={voucher.id}
                                        secondaryAction={renderDownloadButton(
                                            voucher
                                        )}
                                    >
                                        <ListItemText
                                            primary={voucher.code}
                                            secondary={`${formatMoney(voucher.initialAmount)} · gültig bis ${formatIsoDate(voucher.validUntil)}`}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        </CardContent>
                    </Card>
                )}

                <Box sx={{ width: '100%' }}>
                    <Typography variant='h5' sx={{ mb: 2 }}>
                        Ausgestellte Gutscheine
                    </Typography>
                    <TextField
                        label='Code suchen'
                        value={localCode}
                        onChange={(e) => setLocalCode(e.target.value)}
                        sx={{ mb: 2, width: { xs: '100%', md: 360 } }}
                    />
                    {!!issuedVouchersError && (
                        <Alert severity='error' sx={{ mb: 2 }}>
                            Die ausgestellten Gutscheine konnten nicht geladen
                            werden.
                        </Alert>
                    )}
                    <StarjackTable<IssuedVoucherDTO>
                        // Remount on search/size change so the table's internal page state resets.
                        key={`${search.code ?? ''}-${search.size}`}
                        count={issuedVouchers?.totalElements ?? 0}
                        items={issuedVouchers?.content ?? []}
                        columns={columns}
                        itemId='id'
                        currentPage={search.page}
                        onPageChange={(page) =>
                            setSearch((prev) => ({ ...prev, page }))
                        }
                        rowsPerPage={search.size}
                        onRowsPerPageChange={(size) =>
                            setSearch((prev) => ({ ...prev, size, page: 0 }))
                        }
                        loading={issuedVouchersLoading}
                        noDataLabel='Noch keine Gutscheine ausgestellt.'
                    />
                </Box>
            </Stack>

            <Dialog
                open={confirmOpen}
                onClose={() => !issuing && setConfirmOpen(false)}
            >
                <DialogTitle>Gutschein(e) ausstellen</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        {quantity === 1
                            ? `1 Gutschein über ${formattedAmount} ausstellen?`
                            : `${quantity} Gutscheine zu je ${formattedAmount} (gesamt ${formattedTotal}) ausstellen?`}
                    </DialogContentText>
                    <DialogContentText>
                        Der Gutschein ist im Webshop und an der Kasse einlösbar.
                        Die Kosten trägt die Bergbahn bei Einlösung.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setConfirmOpen(false)}
                        disabled={issuing}
                    >
                        Abbrechen
                    </Button>
                    <LoadingButton
                        variant='contained'
                        onClick={onConfirmIssue}
                        loading={issuing}
                    >
                        Ausstellen
                    </LoadingButton>
                </DialogActions>
            </Dialog>

            <Dialog
                open={previewOpen}
                onClose={onClosePreview}
                maxWidth='md'
                fullWidth
            >
                <DialogTitle>Vorschau</DialogTitle>
                <DialogContent>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            minHeight: 400,
                        }}
                    >
                        {previewLoading && <CircularProgress />}
                        {!previewLoading && previewUrl && (
                            <img
                                src={previewUrl}
                                alt='Vorschau'
                                style={{
                                    maxWidth: '100%',
                                    maxHeight: '70vh',
                                    objectFit: 'contain',
                                }}
                            />
                        )}
                        {!previewLoading && !previewUrl && (
                            <Typography color='text.secondary'>
                                Keine Vorschau verfügbar.
                            </Typography>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClosePreview}>Schließen</Button>
                </DialogActions>
            </Dialog>
        </Fragment>
    );
};
